import { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiDownload, FiX, FiSearch, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import DepartmentSelect from '../../components/DepartmentSelect';

const API = '/api/v1/ojt';

const FILE_FIELDS = [
  { key: 'training_need_assessment_file', label: 'Training Need Assessment Form' },
  { key: 'ojt_form_file', label: 'OJT Form' },
  { key: 'post_course_evaluation_file', label: 'Post Course Evaluation' },
];

const emptyForm = {
  employee_id: '', employee_name: '', department: '',
  course_name: '', date: '', course_cost: '', oul_approval: false,
  ceo_approval: false, fca_approval: false,
};

export default function OnJobTraining() {
  const { token } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', record? }
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterDept) params.append('department', filterDept);
      const res = await fetch(`${API}?${params}`, { headers: headers() });
      const data = await res.json();
      setRecords(data.records || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, filterDept, headers]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openAdd = () => { setForm(emptyForm); setModal({ mode: 'add' }); };
  const openEdit = (r) => { setForm({ ...r, oul_approval: !!r.oul_approval, ceo_approval: !!r.ceo_approval, fca_approval: !!r.fca_approval, course_cost: r.course_cost || '' }); setModal({ mode: 'edit', record: r }); };

  const handleSave = async () => {
    if (!form.employee_id || !form.employee_name || !form.department || !form.course_name || !form.date) {
      alert('Please fill all required fields.'); return;
    }
    setSaving(true);
    try {
      const url = modal.mode === 'add' ? API : `${API}/${modal.record.id}`;
      const method = modal.mode === 'add' ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      setModal(null);
      fetchRecords();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this OJT record?')) return;
    await fetch(`${API}/${id}`, { method: 'DELETE', headers: headers() });
    fetchRecords();
    if (detailRecord?.id === id) setDetailRecord(null);
  };

  const handleUpload = async (recordId, field, file) => {
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/${recordId}/upload/${field}`, { method: 'POST', headers: headers(), body: fd });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      // Refresh detail and list
      const dr = await fetch(`${API}/${recordId}`, { headers: headers() });
      const d = await dr.json();
      setDetailRecord(d.record);
      fetchRecords();
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingField(null);
    }
  };

  const handleViewFile = async (recordId, field) => {
    try {
      const res = await fetch(`${API}/${recordId}/download/${field}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to open file');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      alert(err.message || 'Failed to open file');
    }
  };

  const handleDeleteFile = async (recordId, field) => {
    await fetch(`${API}/${recordId}/file/${field}`, { method: 'DELETE', headers: headers() });
    const dr = await fetch(`${API}/${recordId}`, { headers: headers() });
    const d = await dr.json();
    setDetailRecord(d.record);
    fetchRecords();
  };

  const openDetail = async (id) => {
    const res = await fetch(`${API}/${id}`, { headers: headers() });
    const d = await res.json();
    setDetailRecord(d.record);
  };

  const set = (k, v) => setForm((f) => {
    const updated = { ...f, [k]: v };
    // Sequential logic: unchecking a step clears all subsequent steps
    if (k === 'oul_approval' && !v) { updated.ceo_approval = false; updated.fca_approval = false; }
    if (k === 'ceo_approval' && !v) { updated.fca_approval = false; }
    return updated;
  });

  return (
    <div>
      {/* Header */}
      <div style={s.headerRow}>
        <h3 style={s.title}>On Job Training</h3>
        <button style={s.addBtn} onClick={openAdd}><FiPlus size={14} /> Add Record</button>
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.searchWrap}>
          <FiSearch size={14} style={s.searchIcon} />
          <input style={s.searchInput} placeholder="Search by employee or course..." value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        <DepartmentSelect
          value={filterDept}
          onChange={setFilterDept}
          placeholder="All Departments"
          style={s.select}
        />
      </div>

      {/* Table */}
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Employee ID', 'Name', 'Department', 'Course Name', 'Date', 'Cost (SAR)', 'OUL Approval', 'CEO Approval', 'FCA Approval', 'Files', ''].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={s.empty}>Loading...</td></tr>
            ) : records.length === 0 ? (
              <tr><td colSpan={11} style={s.empty}>No records found.</td></tr>
            ) : records.map((r) => {
              const fileCount = FILE_FIELDS.filter((f) => r[f.key]).length;
              return (
                <tr key={r.id} style={s.tr} onClick={() => openDetail(r.id)}>
                  <td style={s.td}>{r.employee_id}</td>
                  <td style={{ ...s.td, fontWeight: 500 }}>{r.employee_name}</td>
                  <td style={s.td}>{r.department}</td>
                  <td style={s.td}>{r.course_name}</td>
                  <td style={s.td}>{r.date}</td>
                  <td style={s.td}>{r.course_cost ? Number(r.course_cost).toLocaleString() : '—'}</td>
                  <td style={s.td}>
                    {r.oul_approval
                      ? <span style={s.badge('#6B8063')}>Approved</span>
                      : <span style={s.badge('#B2AB9C')}>Pending</span>}
                  </td>
                  <td style={s.td}>
                    {r.ceo_approval
                      ? <span style={s.badge('#6B8063')}>Approved</span>
                      : <span style={s.badge('#B2AB9C')}>Pending</span>}
                  </td>
                  <td style={s.td}>
                    {r.fca_approval
                      ? <span style={s.badge('#6B8063')}>Approved</span>
                      : <span style={s.badge('#B2AB9C')}>Pending</span>}
                  </td>
                  <td style={s.td}>
                    <span style={s.fileCount}>{fileCount}/3</span>
                  </td>
                  <td style={s.td} onClick={(e) => e.stopPropagation()}>
                    <div style={s.actions}>
                      <button style={s.iconBtn} onClick={() => openEdit(r)}><FiEdit2 size={13} /></button>
                      <button style={{ ...s.iconBtn, color: '#C0392B' }} onClick={() => handleDelete(r.id)}><FiTrash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={s.count}>{records.length} record{records.length !== 1 ? 's' : ''}</div>

      {/* Add/Edit Modal */}
      {modal && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{modal.mode === 'add' ? 'Add OJT Record' : 'Edit OJT Record'}</h3>
              <button style={s.closeBtn} onClick={() => setModal(null)}><FiX size={18} /></button>
            </div>
            <div style={s.modalBody}>
              <div style={s.grid2}>
                <div style={s.fg}>
                  <label style={s.label}>Employee ID *</label>
                  <input style={s.input} value={form.employee_id} onChange={(e) => set('employee_id', e.target.value)} placeholder="e.g. EMP-001" />
                </div>
                <div style={s.fg}>
                  <label style={s.label}>Employee Name *</label>
                  <input style={s.input} value={form.employee_name} onChange={(e) => set('employee_name', e.target.value)} placeholder="Full name" />
                </div>
                <div style={s.fg}>
                  <label style={s.label}>Department *</label>
                  <DepartmentSelect value={form.department} onChange={(v) => set('department', v)} style={s.input} />
                </div>
                <div style={s.fg}>
                  <label style={s.label}>Course Name *</label>
                  <input style={s.input} value={form.course_name} onChange={(e) => set('course_name', e.target.value)} placeholder="Course title" />
                </div>
                <div style={s.fg}>
                  <label style={s.label}>Date *</label>
                  <input style={s.input} type="date" value={form.date} onChange={(e) => set('date', e.target.value)} />
                </div>
                <div style={s.fg}>
                  <label style={s.label}>Course Cost (SAR)</label>
                  <input style={s.input} type="number" min="0" value={form.course_cost} onChange={(e) => set('course_cost', e.target.value)} placeholder="0" />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#B2AB9C', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '12px' }}>Approval Steps</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                  {[
                    { key: 'oul_approval', label: 'OUL Approval', step: 1, enabled: true },
                    { key: 'ceo_approval', label: 'CEO Approval', step: 2, enabled: !!form.oul_approval },
                    { key: 'fca_approval', label: 'FCA Approval', step: 3, enabled: !!form.ceo_approval },
                  ].map(({ key, label, step, enabled }, i) => {
                    const checked = !!form[key];
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <div
                          onClick={() => enabled && set(key, !checked)}
                          style={{
                            flex: 1, padding: '12px 14px', borderRadius: '10px', border: `2px solid ${checked ? '#6B8063' : enabled ? '#e8e3da' : '#f0ece4'}`,
                            backgroundColor: checked ? '#e8f0e5' : enabled ? '#fff' : '#faf8f4',
                            cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                              backgroundColor: checked ? '#6B8063' : enabled ? '#fff' : '#f0ece4',
                              border: `2px solid ${checked ? '#6B8063' : enabled ? '#e8e3da' : '#f0ece4'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.7rem', fontWeight: 700, color: checked ? '#fff' : '#B2AB9C',
                            }}>
                              {checked ? '✓' : step}
                            </div>
                            <div>
                              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: checked ? '#2B362E' : enabled ? '#2B362E' : '#B2AB9C' }}>{label}</div>
                              <div style={{ fontSize: '0.7rem', color: checked ? '#6B8063' : enabled ? '#B2AB9C' : '#d0ccc4' }}>
                                {checked ? 'Approved' : enabled ? 'Click to approve' : 'Complete previous step'}
                              </div>
                            </div>
                          </div>
                        </div>
                        {i < 2 && (
                          <div style={{ width: '24px', flexShrink: 0, height: '2px', backgroundColor: checked ? '#6B8063' : '#e8e3da' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.cancelBtn} onClick={() => setModal(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail / File Upload Panel */}
      {detailRecord && (
        <div style={s.overlay} onClick={() => setDetailRecord(null)}>
          <div style={{ ...s.modal, maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{detailRecord.employee_name} — {detailRecord.course_name}</h3>
              <button style={s.closeBtn} onClick={() => setDetailRecord(null)}><FiX size={18} /></button>
            </div>
            <div style={s.modalBody}>
              {/* Info summary */}
              <div style={s.infoGrid}>
                {[
                  ['Employee ID', detailRecord.employee_id],
                  ['Department', detailRecord.department],
                  ['Date', detailRecord.date],
                  ['Cost (SAR)', detailRecord.course_cost ? Number(detailRecord.course_cost).toLocaleString() : '—'],
                  ['OUL Approval', detailRecord.oul_approval ? 'Approved' : 'Pending'],
                  ['CEO Approval', detailRecord.ceo_approval ? 'Approved' : 'Pending'],
                  ['FCA Approval', detailRecord.fca_approval ? 'Approved' : 'Pending'],
                ].map(([k, v]) => (
                  <div key={k} style={s.infoItem}>
                    <span style={s.infoLabel}>{k}</span>
                    <span style={s.infoValue}>{v}</span>
                  </div>
                ))}
              </div>

              {/* File uploads */}
              <div style={{ marginTop: '20px' }}>
                <div style={s.sectionLabel}>Documents</div>
                {FILE_FIELDS.map((f) => (
                  <div key={f.key} style={s.fileRow}>
                    <div style={s.fileLabel}>
                      {detailRecord[f.key]
                        ? <FiCheck size={13} style={{ color: '#6B8063', marginRight: '6px' }} />
                        : <div style={{ width: '13px', marginRight: '6px' }} />}
                      {f.label}
                    </div>
                    <div style={s.fileActions}>
                      {detailRecord[f.key] && (
                        <>
                          <button style={s.fileLinkBtn} onClick={() => handleViewFile(detailRecord.id, f.key)}>
                            <FiDownload size={13} /> View
                          </button>
                          <button style={s.fileDeleteBtn} onClick={() => handleDeleteFile(detailRecord.id, f.key)}>
                            <FiX size={12} />
                          </button>
                        </>
                      )}
                      <label style={s.uploadBtn}>
                        {uploadingField === f.key ? 'Uploading...' : detailRecord[f.key] ? 'Replace' : <><FiUpload size={12} /> Upload</>}
                        <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                          onChange={(e) => e.target.files[0] && handleUpload(detailRecord.id, f.key, e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  title: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2B362E' },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', backgroundColor: '#2B362E', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
  },
  filters: { display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, minWidth: '200px' },
  searchIcon: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#B2AB9C' },
  searchInput: {
    width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e8e3da',
    borderRadius: '8px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box',
  },
  select: { padding: '8px 12px', border: '1px solid #e8e3da', borderRadius: '8px', fontSize: '0.85rem', outline: 'none', backgroundColor: '#fff' },
  tableWrap: { overflowX: 'auto', borderRadius: '10px', border: '1px solid #e8e3da', backgroundColor: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6b6b6b',
    borderBottom: '1px solid #e8e3da', backgroundColor: '#F5F0E8', whiteSpace: 'nowrap',
  },
  tr: { cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '11px 14px', borderBottom: '1px solid #f0ece4', color: '#2B362E', verticalAlign: 'middle' },
  empty: { padding: '32px', textAlign: 'center', color: '#B2AB9C' },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
    backgroundColor: color + '22', color, fontSize: '0.72rem', fontWeight: 600,
  }),
  fileCount: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#f0ece4', fontSize: '0.72rem', fontWeight: 600, color: '#6b6b6b' },
  actions: { display: 'flex', gap: '6px' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b6b6b', display: 'flex', alignItems: 'center' },
  count: { marginTop: '8px', fontSize: '0.78rem', color: '#B2AB9C' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '780px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
  modalHeader: { padding: '20px 24px 16px', borderBottom: '1px solid #f0ece4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#2B362E' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b6b6b', display: 'flex', alignItems: 'center' },
  modalBody: { padding: '20px 24px' },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #f0ece4', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  fg: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '0.78rem', fontWeight: 600, color: '#2B362E' },
  input: { padding: '8px 12px', border: '1px solid #e8e3da', borderRadius: '8px', fontSize: '0.85rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  cancelBtn: { padding: '8px 20px', border: '1px solid #e8e3da', borderRadius: '8px', background: '#fff', fontSize: '0.85rem', cursor: 'pointer', color: '#6b6b6b' },
  saveBtn: { padding: '8px 24px', backgroundColor: '#2B362E', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  infoItem: { padding: '10px 12px', backgroundColor: '#faf8f4', borderRadius: '8px', border: '1px solid #f0ece4' },
  infoLabel: { display: 'block', fontSize: '0.65rem', color: '#B2AB9C', textTransform: 'uppercase', fontWeight: 600, marginBottom: '2px' },
  infoValue: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#2B362E' },
  sectionLabel: { fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', color: '#B2AB9C', letterSpacing: '0.5px', marginBottom: '10px' },
  fileRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0ece4' },
  fileLabel: { fontSize: '0.85rem', color: '#2B362E', display: 'flex', alignItems: 'center' },
  fileActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  fileLinkBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6px', backgroundColor: '#f0ece4', color: '#2B362E', fontSize: '0.78rem', fontWeight: 500, border: 'none', cursor: 'pointer' },
  fileDeleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', display: 'flex', alignItems: 'center' },
  uploadBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 12px', backgroundColor: '#2B362E', color: '#fff', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' },
};
